// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// optimizerWeekRestore (#422) — decide which week a RESTORED Optimizer grid
// should open on.
//
// The Optimizer persists each grid's `weekStartDate` in localStorage
// (`scheduler.optimizer.gridWorkspace`) so a refresh puts you back on the week
// you were working. That is the whole intent — "same week after a reload".
//
// ⚠️ THE BUG THIS FIXES. `onWeekBehind` blocks NAVIGATING back past the
// attributes window (MONGODB_ATTRIBUTES_DAYS_PAST), but nothing re-checked a
// week that was already stored and had since aged out. A week saved on Friday is
// out of range three weeks later, and the guard only ever covered the
// transition, never the resting state.
//
// The consequence is silent and nasty: the app does not FETCH attributes older
// than the window, so on such a week a Custom availability note SAVES correctly
// and can never be read back. The box is empty next time you look —
// indistinguishable from "it didn't save". That is #415, which cost a day and
// was very nearly filed as a production blocker. It also drives every save down
// the CREATE path (the app can't see the day's existing record), so the same
// installer-day accumulates duplicate Attributes rows — five, in the case we
// found.
//
// Leland's call (2026-07-31), and it is the right one: advance them to TODAY,
// silently. "It's built for returning them to the current week they are on
// after refreshing, that's all." A week from two weeks ago isn't a week they
// chose to be on; it's a stale artifact. Today is also what a fresh login would
// have given them, so there is nothing to explain.
//
// Advance to TODAY rather than to the oldest allowed week: the boundary week is
// an arbitrary week the user never picked, and landing there would be its own
// small surprise.
//
// Pure + importable so the tests exercise the real decision rather than a
// re-implementation of it (CLAUDE.md; the #339 / #426 pattern).
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

import { MILLISECONDS_PER_DAY, MONGODB_ATTRIBUTES_DAYS_PAST } from "./keys";
import { getMondayOf, getMondayOfThisWeek } from "./weekDates";

/**
 * Is this Monday older than the app's attributes fetch window?
 *
 * Deliberately mirrors `isWeekBeyondLimit` in app.context.js — same cutoff, same
 * comparison — so the restore guard and the back-navigation guard cannot drift.
 * Their disagreeing is exactly what created #422.
 *
 * @param {Date} monday       the week's Monday
 * @param {Date} [today]      injectable for tests
 * @param {number} [daysPast] injectable; defaults to the app's real window
 * @returns {boolean} true when the week can no longer be read back
 */
export function isWeekBeyondAttributesWindow(
  monday,
  today = new Date(),
  daysPast = MONGODB_ATTRIBUTES_DAYS_PAST
) {
  if (!(monday instanceof Date) || isNaN(monday.getTime())) return false;
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);
  const cutoff = new Date(start.getTime() - daysPast * MILLISECONDS_PER_DAY);
  cutoff.setHours(0, 0, 0, 0);
  return monday < cutoff;
}

/**
 * The week a restored grid should actually open on.
 *
 * @param {*} storedWeekStart  whatever was in localStorage (string | Date)
 * @param {Date} [today]       injectable for tests
 * @param {number} [daysPast]  injectable
 * @returns {Date} a Monday at local midnight — the stored week if it is still
 *   readable, otherwise THIS week. Unparseable input also falls back to this
 *   week rather than producing an Invalid Date the grid would render blank.
 */
export function resolveRestoredWeek(
  storedWeekStart,
  today = new Date(),
  daysPast = MONGODB_ATTRIBUTES_DAYS_PAST
) {
  const parsed = new Date(storedWeekStart);
  if (isNaN(parsed.getTime())) return getMondayOf(today);
  const monday = getMondayOf(parsed);
  if (isWeekBeyondAttributesWindow(monday, today, daysPast)) {
    return getMondayOf(today);
  }
  return monday;
}

/**
 * Apply the rule across a whole restored workspace.
 *
 * ⚠️ PER-GRID, deliberately. Two grids sitting on DIFFERENT weeks is a real
 * feature (the side-by-side week comparison), so only the grids that have aged
 * out are advanced — an in-window sibling keeps its week. Collapsing them all to
 * today would silently destroy a comparison the user had set up, trading one
 * quiet surprise for another.
 *
 * @param {Array<{weekStartDate: *}>} gridEntries
 * @param {Date} [today]
 * @param {number} [daysPast]
 * @returns {Array} the same entries with `weekStartDate` resolved to a Monday
 */
export function resolveRestoredWorkspace(
  gridEntries,
  today = new Date(),
  daysPast = MONGODB_ATTRIBUTES_DAYS_PAST
) {
  if (!Array.isArray(gridEntries)) return [];
  return gridEntries.map((g) => ({
    ...g,
    weekStartDate: resolveRestoredWeek(g?.weekStartDate, today, daysPast),
  }));
}

export const __testables__ = { getMondayOfThisWeek };
